import { useQuery } from '@tanstack/react-query';

import { useApiClient } from './api-context';
import { isApiError, unwrap } from './errors';
import { queryKeys } from './query-keys';
import type { Schema } from './types';

/**
 * The signed-in person. A 401 has already sent the browser to the sign-in (see `sessionMiddleware`), so while the
 * user is unknown the page just keeps waiting: `user` stays undefined. Any other failure is returned as `error`.
 */
export function useCurrentUser(): {
  user: Schema<'Me'> | undefined;
  /** Set when /me failed with anything but a 401 (which is already on its way to the sign-in). */
  error: Error | undefined;
  retry: () => void;
} {
  const client = useApiClient();
  const { data, error, refetch } = useQuery({
    queryKey: queryKeys.me(),
    queryFn: () => unwrap(client.GET('/me')),
    staleTime: Infinity,
  });
  const failed = error !== null && !(isApiError(error) && error.status === 401);
  return { user: data, error: failed ? error : undefined, retry: () => void refetch() };
}
